import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { useEffectEvent } from 'use-effect-event'

import { useSession } from '@/legacy-src/contexts/session-context'

import Loader from './Loader'

export type AuthType = boolean | { redirectToOriginal?: boolean; loginPage?: string }

type Props = {
  auth: AuthType
  children: React.ReactNode
}

const getRedirectUrlParams = (auth: AuthType) => {
  if (typeof auth === 'object') {
    return {
      redirectToOriginal: auth.redirectToOriginal ?? true,
      loginPage: auth.loginPage ?? 'login',
    }
  }

  return {
    redirectToOriginal: true,
    loginPage: 'login',
  }
}

// Simple Auth component that replaces withAuth
const Auth: React.FC<Props> = ({ children, auth }) => {
  const { user, isLoading, isAuthenticating } = useSession()
  const isInProgress = isAuthenticating || isLoading
  const router = useRouter()

  const hasValidUser = Boolean(user?.id)

  const handleUser = useEffectEvent(() => {
    if (isInProgress) {
      return
    }

    if (!hasValidUser) {
      const { loginPage, redirectToOriginal } = getRedirectUrlParams(auth)

      if (redirectToOriginal) {
        router.replace(`/${loginPage}?redirectUrl=${encodeURIComponent(router.asPath)}`)
        return
      }

      router.replace(`/${loginPage}`)
    }
  })

  useEffect(() => {
    handleUser()
    // effect events shouldn't be included in the dependency array, as they are guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isInProgress])

  if (hasValidUser) {
    return <>{children}</>
  }

  // Session is being fetched, or no user.
  // If no user, useEffect() will redirect.
  return <Loader visible color="light-gray" />
}

export default Auth
